import React, { useState, useEffect, useMemo } from "react";
import { Pencil, RotateCcw, X, Save, Info } from "lucide-react";
import { colones } from "@/lib/api";

/**
 * Modal para editar manualmente las horas y días de un empleado en una
 * planilla (semanal o quincenal). Se usa en dos escenarios:
 *   1) Empleado con `planilla_editable=true` (permanente).
 *   2) Planillas históricas anteriores a la fecha de registro de la empresa
 *      (para cargar historial retroactivo y calcular aguinaldo).
 *
 * Campos:
 *   - Horas Ord/Ext por jornada (Diurna/Mixta/Nocturna) → sobrescribe la
 *     acumulación desde asistencia.
 *   - Horas trabajadas en feriado → pago 1x adicional (doble feriado).
 *   - Días Libres, Días de Feriado pagados, Días de Incapacidad CCSS, Días
 *     de Vacaciones, Días de Licencia → cada rubro suma al bruto según reglas
 *     CR (día libre/vacación/feriado = 8h; incapacidad CCSS = 4h × primeros 3
 *     días; licencia = 8h × 50%).
 */
export default function PlanillaEditHorasModal({ open, onClose, empleado, calcActual, overrideActual, onSave, onReset, historico }) {
  const defaults = useMemo(() => {
    const ord = { Diurna: 0, Mixta: 0, Nocturna: 0 };
    const ext = { Diurna: 0, Mixta: 0, Nocturna: 0 };
    let feriado = 0;
    const dias = { dias_libres: 0, dias_feriado: 0, dias_incapacidad_ccss: 0, dias_vacaciones: 0, dias_licencia: 0 };
    let ccss_override = "";
    let rebajos_override = "";
    if (overrideActual) {
      Object.assign(ord, overrideActual.ord || {});
      Object.assign(ext, overrideActual.ext || {});
      feriado = Number(overrideActual.feriado) || 0;
      for (const k of Object.keys(dias)) {
        if (overrideActual[k] !== undefined && overrideActual[k] !== null) dias[k] = Number(overrideActual[k]) || 0;
      }
      if (overrideActual.ccss_override !== undefined && overrideActual.ccss_override !== null && overrideActual.ccss_override !== "") ccss_override = Number(overrideActual.ccss_override);
      if (overrideActual.rebajos_override !== undefined && overrideActual.rebajos_override !== null && overrideActual.rebajos_override !== "") rebajos_override = Number(overrideActual.rebajos_override);
    } else if (calcActual) {
      for (const j of ["Diurna", "Mixta", "Nocturna"]) {
        ord[j] = +(calcActual.byJornadaOrd?.[j]?.hrs || 0).toFixed(2);
        ext[j] = +(calcActual.byJornadaExt?.[j]?.hrs || 0).toFixed(2);
      }
      const salario = Number(empleado?.salario_base) || 0;
      const tarifaDiurna = salario / 30 / 8;
      feriado = tarifaDiurna > 0 ? +(((calcActual.breakdown?.feriado?.monto || 0) / tarifaDiurna).toFixed(2)) : 0;
      // Pre-poblar días desde asistencia si existen
      dias.dias_libres = calcActual.breakdown?.diaLibre?.dias || 0;
      dias.dias_feriado = calcActual.breakdown?.feriado?.dias || 0;
      dias.dias_incapacidad_ccss = calcActual.breakdown?.incapacidadCCSS?.dias || 0;
      dias.dias_vacaciones = calcActual.breakdown?.vacaciones?.dias || 0;
      dias.dias_licencia = calcActual.breakdown?.licencia?.dias || 0;
    }
    return { ord, ext, feriado, dias, ccss_override, rebajos_override };
  }, [calcActual, overrideActual, empleado]);

  const [ord, setOrd] = useState(defaults.ord);
  const [ext, setExt] = useState(defaults.ext);
  const [feriado, setFeriado] = useState(defaults.feriado);
  const [dias, setDias] = useState(defaults.dias);
  const [ccssOv, setCcssOv] = useState(defaults.ccss_override);
  const [rebajosOv, setRebajosOv] = useState(defaults.rebajos_override);

  useEffect(() => {
    if (open) {
      setOrd(defaults.ord);
      setExt(defaults.ext);
      setFeriado(defaults.feriado);
      setDias(defaults.dias);
      setCcssOv(defaults.ccss_override);
      setRebajosOv(defaults.rebajos_override);
    }
  }, [open, defaults]);

  if (!open) return null;

  const salario = Number(empleado?.salario_base) || 0;
  const tarifaOrd = (jornada) => {
    const h = { Diurna: 8, Mixta: 7, Nocturna: 6 }[jornada] || 8;
    return salario / 30 / h;
  };
  const tarifaExt = (jornada) => tarifaOrd(jornada) * 1.5;
  const tarifaDiurna = salario / 30 / 8;

  const previewOrd = ["Diurna", "Mixta", "Nocturna"].reduce((s, j) => s + (Number(ord[j]) || 0) * tarifaOrd(j), 0);
  const previewExt = ["Diurna", "Mixta", "Nocturna"].reduce((s, j) => s + (Number(ext[j]) || 0) * tarifaExt(j), 0);
  const previewFeriado = (Number(feriado) || 0) * tarifaDiurna;
  // Previews de días (según reglas CR)
  const previewDiaLibre = (Number(dias.dias_libres) || 0) * 8 * tarifaDiurna;
  const previewDiaFeriado = (Number(dias.dias_feriado) || 0) * 8 * tarifaDiurna;
  const diasIncCCSS = Number(dias.dias_incapacidad_ccss) || 0;
  const previewInc = Math.min(diasIncCCSS, 3) * 4 * tarifaDiurna;
  const previewVac = (Number(dias.dias_vacaciones) || 0) * 8 * tarifaDiurna;
  const previewLic = (Number(dias.dias_licencia) || 0) * 8 * tarifaDiurna * 0.5;
  const previewBrutoDias = previewDiaLibre + previewDiaFeriado + previewInc + previewVac + previewLic;

  const setOrdVal = (j, v) => setOrd(prev => ({ ...prev, [j]: v }));
  const setExtVal = (j, v) => setExt(prev => ({ ...prev, [j]: v }));
  const setDiaVal = (k, v) => setDias(prev => ({ ...prev, [k]: v }));

  const handleSave = () => {
    const cleanOrd = {}, cleanExt = {};
    for (const j of ["Diurna", "Mixta", "Nocturna"]) {
      cleanOrd[j] = Math.max(0, Number(ord[j]) || 0);
      cleanExt[j] = Math.max(0, Number(ext[j]) || 0);
    }
    const cleanDias = {
      dias_libres: Math.max(0, Number(dias.dias_libres) || 0),
      dias_feriado: Math.max(0, Number(dias.dias_feriado) || 0),
      dias_incapacidad_ccss: Math.max(0, Number(dias.dias_incapacidad_ccss) || 0),
      dias_vacaciones: Math.max(0, Number(dias.dias_vacaciones) || 0),
      dias_licencia: Math.max(0, Number(dias.dias_licencia) || 0),
    };
    // Overrides opcionales de CCSS y Rebajos (string vacío = usar cálculo automático).
    const ccssPayload = (ccssOv === "" || ccssOv === null || ccssOv === undefined) ? null : Math.max(0, Number(ccssOv) || 0);
    const rebajosPayload = (rebajosOv === "" || rebajosOv === null || rebajosOv === undefined) ? null : Math.max(0, Number(rebajosOv) || 0);
    onSave({
      ord: cleanOrd,
      ext: cleanExt,
      feriado: Math.max(0, Number(feriado) || 0),
      ...cleanDias,
      ccss_override: ccssPayload,
      rebajos_override: rebajosPayload,
    });
  };

  const handleReset = () => {
    if (!window.confirm("¿Restaurar valores automáticos desde asistencia?")) return;
    onReset();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="np-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="planilla-edit-modal">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E6EFEA] text-primary-color flex items-center justify-center flex-shrink-0">
              <Pencil size={20} strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">
                Editar planilla — {empleado?.nombre} {empleado?.apellidos}
                {historico && <span className="ml-2 text-xs font-medium bg-[#FFF7E6] text-[#B57F1A] px-2 py-0.5 rounded-full align-middle">Histórico</span>}
              </h3>
              <p className="text-xs text-secondary-c mt-0.5">
                {historico
                  ? "Planilla anterior a la fecha de registro de la empresa. Ingresá los valores históricos para el cálculo de aguinaldo."
                  : "Sobrescribe las horas calculadas desde asistencia. Los cambios solo aplican a esta planilla."}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="np-btn np-btn-outline p-1.5" data-testid="edit-horas-close"><X size={16} /></button>
        </div>

        {/* Sección: Horas trabajadas por jornada */}
        <div className="mb-4">
          <div className="text-xs font-semibold text-secondary-c uppercase tracking-wide mb-2">Horas trabajadas</div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-xs font-medium text-secondary-c">Jornada</div>
            <div className="text-xs font-medium text-secondary-c">Hrs Ord</div>
            <div className="text-xs font-medium text-secondary-c">Hrs Ext</div>

            {["Diurna", "Mixta", "Nocturna"].map(j => (
              <React.Fragment key={j}>
                <div className="flex items-center text-sm font-medium">{j}</div>
                <input
                  type="number" step="0.01" min="0"
                  value={ord[j]}
                  onChange={e => setOrdVal(j, e.target.value)}
                  className="np-input"
                  data-testid={`edit-horas-ord-${j.toLowerCase()}`}
                />
                <input
                  type="number" step="0.01" min="0"
                  value={ext[j]}
                  onChange={e => setExtVal(j, e.target.value)}
                  className="np-input"
                  data-testid={`edit-horas-ext-${j.toLowerCase()}`}
                />
              </React.Fragment>
            ))}
          </div>
          <div>
            <label className="text-xs font-medium text-secondary-c block mb-1">
              Horas trabajadas en feriado <span className="text-secondary-c">(pago 1× adicional a tarifa diurna)</span>
            </label>
            <input
              type="number" step="0.01" min="0"
              value={feriado}
              onChange={e => setFeriado(e.target.value)}
              className="np-input max-w-[200px]"
              data-testid="edit-horas-feriado"
            />
          </div>
        </div>

        {/* Sección: Días manuales */}
        <div className="mb-4 border-t border-c pt-4">
          <div className="text-xs font-semibold text-secondary-c uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <Info size={12} /> Días adicionales (opcional)
          </div>
          <p className="text-[11px] text-secondary-c mb-3">Ingresá la cantidad de días por cada rubro; se pagan a tarifa diurna según las reglas del Código de Trabajo.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-secondary-c block mb-1">Días libres</label>
              <input type="number" step="1" min="0" value={dias.dias_libres} onChange={e => setDiaVal("dias_libres", e.target.value)} className="np-input" data-testid="edit-dias-libres" />
            </div>
            <div>
              <label className="text-xs font-medium text-secondary-c block mb-1">Días feriado</label>
              <input type="number" step="1" min="0" value={dias.dias_feriado} onChange={e => setDiaVal("dias_feriado", e.target.value)} className="np-input" data-testid="edit-dias-feriado" />
            </div>
            <div>
              <label className="text-xs font-medium text-secondary-c block mb-1">Días incapacidad CCSS</label>
              <input type="number" step="1" min="0" value={dias.dias_incapacidad_ccss} onChange={e => setDiaVal("dias_incapacidad_ccss", e.target.value)} className="np-input" data-testid="edit-dias-incapacidad" />
            </div>
            <div>
              <label className="text-xs font-medium text-secondary-c block mb-1">Días vacaciones</label>
              <input type="number" step="1" min="0" value={dias.dias_vacaciones} onChange={e => setDiaVal("dias_vacaciones", e.target.value)} className="np-input" data-testid="edit-dias-vacaciones" />
            </div>
            <div>
              <label className="text-xs font-medium text-secondary-c block mb-1">Días licencia</label>
              <input type="number" step="1" min="0" value={dias.dias_licencia} onChange={e => setDiaVal("dias_licencia", e.target.value)} className="np-input" data-testid="edit-dias-licencia" />
            </div>
          </div>
        </div>

        {/* Sección: Ajustes de deducciones (opcional) */}
        <div className="mb-4 border-t border-c pt-4">
          <div className="text-xs font-semibold text-secondary-c uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <Info size={12} /> Ajustes de deducciones (opcional)
          </div>
          <p className="text-[11px] text-secondary-c mb-3">
            Dejá los campos vacíos para usar el cálculo automático. Para planillas históricas, podés ingresar los valores que efectivamente se descontaron.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-secondary-c block mb-1">CCSS (monto en ₡)</label>
              <input
                type="number" step="0.01" min="0"
                value={ccssOv}
                onChange={e => setCcssOv(e.target.value)}
                placeholder="Auto"
                className="np-input"
                data-testid="edit-ccss-override"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-secondary-c block mb-1">Rebajos (monto total en ₡)</label>
              <input
                type="number" step="0.01" min="0"
                value={rebajosOv}
                onChange={e => setRebajosOv(e.target.value)}
                placeholder="Auto"
                className="np-input"
                data-testid="edit-rebajos-override"
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-[#FAFAF7] border border-c rounded-xl p-3.5 mb-5 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-secondary-c">Monto Ord:</span><strong>{colones(previewOrd)}</strong></div>
          <div className="flex justify-between"><span className="text-secondary-c">Monto Ext:</span><strong className="text-warning">{colones(previewExt)}</strong></div>
          <div className="flex justify-between"><span className="text-secondary-c">Pago doble Feriado (horas):</span><strong>{colones(previewFeriado)}</strong></div>
          {previewDiaLibre > 0 && <div className="flex justify-between"><span className="text-secondary-c">Días libres:</span><strong>{colones(previewDiaLibre)}</strong></div>}
          {previewDiaFeriado > 0 && <div className="flex justify-between"><span className="text-secondary-c">Días feriado:</span><strong>{colones(previewDiaFeriado)}</strong></div>}
          {previewInc > 0 && <div className="flex justify-between"><span className="text-secondary-c">Incapacidad CCSS:</span><strong>{colones(previewInc)}</strong></div>}
          {previewVac > 0 && <div className="flex justify-between"><span className="text-secondary-c">Vacaciones:</span><strong>{colones(previewVac)}</strong></div>}
          {previewLic > 0 && <div className="flex justify-between"><span className="text-secondary-c">Licencia (50%):</span><strong>{colones(previewLic)}</strong></div>}
          <div className="flex justify-between pt-1.5 mt-1.5 border-t border-c"><span className="text-secondary-c font-semibold">Bruto estimado:</span><strong className="text-success">{colones(previewOrd + previewExt + previewFeriado + previewBrutoDias)}</strong></div>
        </div>

        <div className="flex justify-between gap-2">
          <button onClick={handleReset} className="np-btn np-btn-outline inline-flex items-center gap-1.5" data-testid="edit-horas-reset">
            <RotateCcw size={14} /> Restaurar asistencia
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="np-btn np-btn-outline" data-testid="edit-horas-cancel">Cancelar</button>
            <button onClick={handleSave} className="np-btn np-btn-primary inline-flex items-center gap-1.5" data-testid="edit-horas-save">
              <Save size={14} /> Guardar cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
